import { useState, useEffect, useCallback } from 'react';
// material
import {
  Card,
  Table,
  Stack,
  Button,
  TableRow,
  TableBody,
  Container,
  Typography,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip
} from '@mui/material';
import { Box } from '@mui/system';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
// components
import Page from '../../components/Page';
import Label from '../../components/Label';
import Scrollbar from '../../components/Scrollbar';
import SearchNotFoundAgreement from '../../components/SearchNotFoundAgreement';
import { UserListHead, UserMoreMenu } from '../../components/_dashboard/user';
import BrudCrumbs from '../../components/BreadCrumbs';
import PageTitle from '../../components/PageHeading';
import { fDate, fDateTime2 } from '../../utils/formatTime';
import AgreementToolbar from './AgreementToolbar';
import { StyledTableCell, StyledTableRow } from '../../utils/StyleTable';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import { getAgreementsList } from '../../store/Slice/Agreement/AgreementSlice';
import useAgreementSelector from '../../store/Slice/Agreement/AgreementSelector';
import { getBankDropdownData, saveApiData } from '../../store/Slice/Global/GlobalSlice';
import { AgreementApi } from '../../utils/Apis';
import SimplePagination from '../../components/SimplePagination';
import useDebounce from '../../utils/useDebounce';
import { fireApi } from '../../utils/FireApi';
import { setIsLoading } from '../../store/Slice/Loader/LoaderSlice';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'bankName', label: 'Bank', alignRight: false },
  { id: 'noofcalls', label: 'No of Branch', alignRight: false },
  { id: 'startOn', label: 'Start On', alignRight: false },
  { id: 'endOn', label: 'End On', alignRight: false },
  { id: 'modifiedOn', label: 'Modified On', alignRight: false },
  { id: 'status', label: 'Status', alignRight: false },
  { id: 'action', label: 'ACTION' }
];

export default function Agreement() {
  const [page, setPage] = useState(0);
  const [page1, setPage1] = useState(1);
  const [order, setOrder] = useState('desc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState(null);
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [endAgreement, setEndAgreement] = useState(false);
  const [agreementId, setAgreementId] = useState(null);
  const [selectStatusValue, setSelectStatusValue] = useState(' ');
  const [filterDatesRange, setFilterDatesRange] = useState([
    {
      startDate: null,
      endDate: null,
      key: 'selection'
    }
  ]);
  const [filterParameter, setFilterParameter] = useState({
    bankId: null
  });
  const [hasNextPage, setHasNextPage] = useState(true);

  const selectStatus = {
    ACTIVE: {
      type_name: 'Active',
      type_code: 'ACTIVE\t',
      type_id: 134
    },
    INACTIVE: {
      type_name: 'Inactive',
      type_code: 'INACTIVE\t',
      type_id: 136
    },
    INCOMPLETE: {
      type_name: 'Incomplete',
      type_code: 'INCOMPLETE\t',
      type_id: 138
    }
  };

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { LoginData } = useLoginSelector();
  const { AgreementListData } = useAgreementSelector();
  const debouncedSearch = useDebounce(filterName, 600);

  const getAgreementsListApi = useCallback(() => {
    let statusResult = '';

    if (selectStatusValue === 134) {
      statusResult = true;
    } else if (selectStatusValue === 136) {
      statusResult = false;
    } else {
      statusResult = null;
    }

    const credentials = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId,
      accessLevelId: LoginData?.accessLevelId,
      entityListRequestModel: {
        searchLiteral: debouncedSearch?.length >= 2 ? debouncedSearch : null,
        pageSize: rowsPerPage,
        sortOrder: order,
        pageNumber: page + 1
      },
      agreementFilterModel: {
        aggrementIsActive: statusResult,
        bankId: filterParameter?.bankId || null,
        fromDate:
          filterDatesRange[0].startDate !== null ? fDateTime2(filterDatesRange[0].startDate) : null,
        toDate:
          filterDatesRange[0].endDate !== null ? fDateTime2(filterDatesRange[0].endDate) : null
      }
    };

    dispatch(getAgreementsList(credentials));
  }, [
    LoginData,
    debouncedSearch,
    order,
    filterDatesRange,
    selectStatusValue,
    filterParameter,
    page,
    rowsPerPage,
    dispatch
  ]);

  useEffect(() => {
    getAgreementsListApi();
  }, [getAgreementsListApi]);

  const getBankDropApi = useCallback(() => {
    if (!LoginData) return;
    const data = { companyId: LoginData?.companyId, userId: LoginData?.userId };
    dispatch(getBankDropdownData(data));
  }, [dispatch, LoginData]);

  useEffect(() => {
    getBankDropApi();
  }, []);

  const openEndAgreementDialog = (e, rowId) => {
    setAgreementId(rowId);
    setEndAgreement(true);
  };

  const handleEndAgreement = async (e) => {
    const credential = {
      agreementId: agreementId,
      companyId: LoginData?.companyId,
      userId: LoginData?.userId
    };
    const result = await dispatch(saveApiData(AgreementApi?.agreementTerminate, credential));
    if (result?.status === 'success') {
      getAgreementsListApi();
      setEndAgreement(false);
    }
  };

  const handleRequestSort = (event, property) => {
    if (property !== 'action') {
      setOrder(orderBy === property && order === 'asc' ? 'desc' : 'asc');
      setOrderBy(property);
    }
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = AgreementListData?.map((n) => n);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - AgreementListData.length) : 0;

  useEffect(() => {
    if (!AgreementListData) {
      setHasNextPage(true);
      return;
    }
    setHasNextPage(AgreementListData.length >= rowsPerPage);
  }, [AgreementListData, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  return (
    <Page title="Partner Managemet System || Agreement">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="pageHeading"
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <PageTitle info="List of Agreement" />
            </Stack>
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>

          <Card>
            <AgreementToolbar
              filterName={filterName}
              setFilterName={setFilterName}
              parent="agreement"
              selectStatusValue={selectStatusValue}
              setSelectStatusValue={setSelectStatusValue}
              filterDatesRange={filterDatesRange}
              setFilterDatesRange={setFilterDatesRange}
              selectStatus={selectStatus}
              setFilterParameter={setFilterParameter}
              filterParameter={filterParameter}
            />

            {/* ========== AGREEMENT LIST VIEW ========== */}
            <Scrollbar>
              <TableContainer sx={{ minWidth: 800, height: '50vh', overflow: 'auto' }}>
                <Table>
                  <UserListHead
                    order={order}
                    orderBy={orderBy}
                    headLabel={TABLE_HEAD}
                    rowCount={AgreementListData?.length}
                    numSelected={selected.length}
                    onRequestSort={handleRequestSort}
                    onSelectAllClick={handleSelectAllClick}
                    parent="aggrement"
                  />
                  <TableBody>
                    {AgreementListData?.length > 0 &&
                      AgreementListData?.map((item, index) => {
                        const isItemSelected = selected.indexOf(item) !== -1;
                        return (
                          <StyledTableRow
                            hover
                            key={index}
                            tabIndex={-1}
                            role="checkbox"
                            selected={isItemSelected}
                            aria-checked={isItemSelected}
                          >
                            <StyledTableCell align="left" style={{ paddingLeft: '25px' }}>
                              {item.bankName ? item.bankName : 'N/A'}
                            </StyledTableCell>
                            <StyledTableCell align="left">
                              {item.noOfBranch ? item.noOfBranch : 'N/A'}
                            </StyledTableCell>
                            <StyledTableCell align="left">
                              {item.aggrementStartDate ? fDate(item.aggrementStartDate) : 'N/A'}
                            </StyledTableCell>
                            <StyledTableCell align="left">
                              {item.aggrementEndDate ? fDate(item.aggrementEndDate) : 'N/A'}
                            </StyledTableCell>
                            <StyledTableCell align="left">
                              {item.lastUpdatedOn ? fDate(item.lastUpdatedOn) : 'N/A'}
                            </StyledTableCell>
                            <StyledTableCell align="left">
                              <Label
                                variant="ghost"
                                color={
                                  (item.aggrementIsActive === null && 'warning') ||
                                  (item.aggrementIsActive === false && 'error') ||
                                  'success'
                                }
                              >
                                {item.aggrementIsActive === null
                                  ? 'Incomplete'
                                  : item.aggrementIsActive === true
                                  ? 'Active'
                                  : 'Inactive'}
                              </Label>
                            </StyledTableCell>
                            <StyledTableCell align="" width={115}>
                              <UserMoreMenu
                                partner={item}
                                rowId={item.agreementId}
                                parent="agreement"
                                bankId={item.bankId}
                                bankName={item.bankName}
                                setEndAgreement={setEndAgreement}
                                openEndAgreementDialog={openEndAgreementDialog}
                                aggrementIsActive={item.aggrementIsActive}
                              />
                            </StyledTableCell>
                          </StyledTableRow>
                        );
                      })}
                  </TableBody>
                  {AgreementListData?.length === 0 && (
                    <TableBody>
                      <TableRow>
                        <StyledTableCell
                          align="center"
                          colSpan={TABLE_HEAD.length + 1}
                          sx={{ py: 3 }}
                        >
                          <SearchNotFoundAgreement searchQuery={filterName} />
                        </StyledTableCell>
                      </TableRow>
                    </TableBody>
                  )}
                </Table>
              </TableContainer>
            </Scrollbar>
            <SimplePagination page={page} setPage={setPage} hasNextPage={hasNextPage} />
          </Card>

          <Dialog
            fullWidth
            className="PopupBox"
            open={endAgreement}
            onClose={(e) => setEndAgreement(false)}
          >
            <DialogTitle className="popupTitle">
              <Typography variant="h6">End Agreement Confirmation</Typography>
              <IconButton onClick={(e) => setEndAgreement(false)} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Typography>Are you sure you want to end this agreement?</Typography>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={(e) => {
                  handleEndAgreement(e);
                }}
                variant="contained"
                className="mainBtn deleteBtn"
              >
                Confirm
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Container>
    </Page>
  );
}
